/**
 * FixedAssetNoteCard - Detaljerad anläggningstillgångsnot
 *
 * Visar en komplett not per tillgångstyp med:
 * - Anskaffningsvärden (ingående, inköp, försäljningar + avancerade fält)
 * - Avskrivningar (ingående, årets + avancerade fält)
 * - Uppskrivningar och nedskrivningar (avancerat)
 * - Summavalidering mot balansräkningen
 * - Avskrivningsmetod och nyttjandeperiod
 *
 * Modellerad efter eDeklarera men med bättre UX.
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle2, AlertCircle } from "lucide-react";
import type { FixedAssetNoteDetail } from "@/lib/api";
import { formatNumber, parseFormattedNumber } from "@/lib/format";

interface FixedAssetNoteCardProps {
  type: "intangible" | "buildings" | "machinery";
  title: string;
  legalRef: string;
  icon: React.ReactNode;
  sieBalance: number;
  data: FixedAssetNoteDetail;
  onChange: (data: FixedAssetNoteDetail) => void;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  usefulLifeHint?: string;
  usefulLifeMax?: number;
  /** Whether this is manual entry (not from SIE file) */
  isManual?: boolean;
}

// Toggle switch (same as Step3Complete)
function ToggleSwitch({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        checked ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      {label && <span className="sr-only">{label}</span>}
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// Indicator component for triggered notes (SIE or manual entry)
function TriggeredNoteBadge({ isManual }: { isManual?: boolean }) {
  if (isManual) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
        Baserat på angivna värden
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
      Identifierad i SIE
    </span>
  );
}

// Compute closing values and carrying amount
function computeClosingAcquisition(d: FixedAssetNoteDetail): number {
  return (d.openingAcquisitionCost || 0)
    + (d.purchases || 0)
    - Math.abs(d.sales || 0)
    + (d.businessAcquisitions || 0)
    + (d.mergers || 0)
    + (d.reclassifications || 0)
    + (d.exchangeRateDifferences || 0);
}

function computeClosingDepreciation(d: FixedAssetNoteDetail): number {
  return (d.openingDepreciation || 0)
    + (d.yearDepreciation || 0)
    + (d.salesDepreciation || 0)
    + (d.reclassificationsDepreciation || 0)
    + (d.exchangeRateDifferencesDepreciation || 0);
}

function computeClosingRevaluations(d: FixedAssetNoteDetail): number {
  return (d.openingRevaluations || 0) + (d.yearRevaluations || 0);
}

function computeClosingWriteDowns(d: FixedAssetNoteDetail): number {
  return (d.openingWriteDowns || 0) + (d.yearWriteDowns || 0);
}

function computeCarryingAmount(d: FixedAssetNoteDetail): number {
  return computeClosingAcquisition(d)
    - Math.abs(computeClosingDepreciation(d))
    + computeClosingRevaluations(d)
    - Math.abs(computeClosingWriteDowns(d));
}

// Number input row with formatted display (thousand separators)
function NoteRow({
  label,
  value,
  onChange,
  placeholder,
  negative,
  bold,
  computed,
  indent,
}: {
  label: string;
  value: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  negative?: boolean;
  bold?: boolean;
  computed?: boolean;
  indent?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [rawInput, setRawInput] = useState("");

  return (
    <div className={`flex items-center justify-between py-1.5 ${bold ? "font-semibold" : ""} ${indent ? "pl-4" : ""}`}>
      <span className={`text-sm ${bold ? "text-gray-900" : "text-gray-700"}`}>
        {negative && !computed ? "−  " : ""}{label}
      </span>
      {computed ? (
        <span className={`text-sm tabular-nums ${bold ? "text-gray-900 font-semibold" : "text-gray-700"}`}>
          {formatNumber(value)} kr
        </span>
      ) : (
        <input
          type="text"
          inputMode="numeric"
          value={editing ? rawInput : (value ? formatNumber(value) : "")}
          onFocus={(e) => {
            setEditing(true);
            setRawInput(value ? String(value) : "");
            setTimeout(() => e.target.select(), 0);
          }}
          onBlur={() => {
            setEditing(false);
            onChange?.(parseFormattedNumber(rawInput));
          }}
          onChange={(e) => setRawInput(e.target.value)}
          placeholder={placeholder || "0"}
          className="input w-36 text-right tabular-nums text-sm py-1"
        />
      )}
    </div>
  );
}

export function FixedAssetNoteCard({
  title,
  legalRef,
  icon,
  sieBalance,
  data,
  onChange,
  enabled,
  onToggle,
  usefulLifeHint,
  usefulLifeMax = 30,
  isManual,
}: FixedAssetNoteCardProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const closingAcquisition = computeClosingAcquisition(data);
  const closingDepreciation = computeClosingDepreciation(data);
  const closingRevaluations = computeClosingRevaluations(data);
  const closingWriteDowns = computeClosingWriteDowns(data);
  const carryingAmount = computeCarryingAmount(data);
  const balanceDiff = Math.abs(carryingAmount - sieBalance);
  const isBalanced = balanceDiff < 2; // allow 1 kr rounding

  const update = (partial: Partial<FixedAssetNoteDetail>) => {
    onChange({ ...data, ...partial });
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-200">
            {icon}
          </div>
          <div>
            <h4 className="font-medium text-gray-900 text-sm">{title}</h4>
            <p className="text-xs text-gray-500">{legalRef}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TriggeredNoteBadge isManual={isManual} />
          <span className="text-sm font-medium text-gray-700 tabular-nums">
            {formatNumber(sieBalance)} kr
          </span>
          <ToggleSwitch checked={enabled} onChange={onToggle} label={title} />
        </div>
      </div>

      {/* Content */}
      {enabled && (
        <div className="px-4 py-4 space-y-5">
          {/* Depreciation method and useful life */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avskrivningsmetod
              </label>
              <select
                value={data.depreciationMethod}
                onChange={(e) => update({ depreciationMethod: e.target.value })}
                className="input text-sm"
              >
                <option value="Linjär">Linjär avskrivning</option>
                <option value="Produktionsberoende">Produktionsberoende</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nyttjandeperiod (år) *
              </label>
              <input
                type="number"
                min="1"
                max={usefulLifeMax}
                value={data.usefulLife || ""}
                onChange={(e) => update({ usefulLife: parseInt(e.target.value) || 0 })}
                className="input w-24 text-sm"
                placeholder="5"
              />
              {usefulLifeHint && (
                <p className="text-xs text-gray-500 mt-1">{usefulLifeHint}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4" />

          {/* Anskaffningsvärden */}
          <div>
            <h5 className="text-sm font-semibold text-gray-900 mb-2">Anskaffningsvärden</h5>
            <div className="bg-gray-50 rounded-lg px-3 py-2 space-y-0.5">
              <NoteRow
                label="Ingående anskaffningsvärden"
                value={data.openingAcquisitionCost}
                onChange={(v) => update({ openingAcquisitionCost: v })}
              />
              <NoteRow
                label="Inköp"
                value={data.purchases}
                onChange={(v) => update({ purchases: v })}
              />
              <NoteRow
                label="Försäljningar/utrangeringar"
                value={data.sales}
                onChange={(v) => update({ sales: v })}
                negative
              />

              {showAdvanced && (
                <>
                  <NoteRow
                    label="Rörelseförvärv"
                    value={data.businessAcquisitions || 0}
                    onChange={(v) => update({ businessAcquisitions: v })}
                    indent
                  />
                  <NoteRow
                    label="Fusion"
                    value={data.mergers || 0}
                    onChange={(v) => update({ mergers: v })}
                    indent
                  />
                  <NoteRow
                    label="Omklassificeringar"
                    value={data.reclassifications || 0}
                    onChange={(v) => update({ reclassifications: v })}
                    indent
                  />
                  <NoteRow
                    label="Omräkningsdifferenser"
                    value={data.exchangeRateDifferences || 0}
                    onChange={(v) => update({ exchangeRateDifferences: v })}
                    indent
                  />
                </>
              )}

              <div className="border-t border-gray-300 mt-1 pt-1">
                <NoteRow
                  label="Utgående anskaffningsvärden"
                  value={closingAcquisition}
                  computed
                  bold
                />
              </div>
            </div>
          </div>

          {/* Avskrivningar */}
          <div>
            <h5 className="text-sm font-semibold text-gray-900 mb-2">Avskrivningar</h5>
            <div className="bg-gray-50 rounded-lg px-3 py-2 space-y-0.5">
              <NoteRow
                label="Ingående avskrivningar"
                value={data.openingDepreciation}
                onChange={(v) => update({ openingDepreciation: v })}
              />
              <NoteRow
                label="Årets avskrivningar"
                value={data.yearDepreciation}
                onChange={(v) => update({ yearDepreciation: v })}
                negative
              />

              {showAdvanced && (
                <>
                  <NoteRow
                    label="Försäljningar/utrangeringar"
                    value={data.salesDepreciation || 0}
                    onChange={(v) => update({ salesDepreciation: v })}
                    indent
                  />
                  <NoteRow
                    label="Omklassificeringar"
                    value={data.reclassificationsDepreciation || 0}
                    onChange={(v) => update({ reclassificationsDepreciation: v })}
                    indent
                  />
                  <NoteRow
                    label="Omräkningsdifferenser"
                    value={data.exchangeRateDifferencesDepreciation || 0}
                    onChange={(v) => update({ exchangeRateDifferencesDepreciation: v })}
                    indent
                  />
                </>
              )}

              <div className="border-t border-gray-300 mt-1 pt-1">
                <NoteRow
                  label="Utgående avskrivningar"
                  value={closingDepreciation}
                  computed
                  bold
                />
              </div>
            </div>
          </div>

          {/* Advanced: Uppskrivningar & Nedskrivningar */}
          {showAdvanced && (
            <>
              <div>
                <h5 className="text-sm font-semibold text-gray-900 mb-2">Uppskrivningar</h5>
                <div className="bg-gray-50 rounded-lg px-3 py-2 space-y-0.5">
                  <NoteRow
                    label="Ingående uppskrivningar"
                    value={data.openingRevaluations || 0}
                    onChange={(v) => update({ openingRevaluations: v })}
                  />
                  <NoteRow
                    label="Årets uppskrivningar"
                    value={data.yearRevaluations || 0}
                    onChange={(v) => update({ yearRevaluations: v })}
                  />
                  <div className="border-t border-gray-300 mt-1 pt-1">
                    <NoteRow
                      label="Utgående uppskrivningar"
                      value={closingRevaluations}
                      computed
                      bold
                    />
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-semibold text-gray-900 mb-2">Nedskrivningar</h5>
                <div className="bg-gray-50 rounded-lg px-3 py-2 space-y-0.5">
                  <NoteRow
                    label="Ingående nedskrivningar"
                    value={data.openingWriteDowns || 0}
                    onChange={(v) => update({ openingWriteDowns: v })}
                  />
                  <NoteRow
                    label="Årets nedskrivningar"
                    value={data.yearWriteDowns || 0}
                    onChange={(v) => update({ yearWriteDowns: v })}
                    negative
                  />
                  <div className="border-t border-gray-300 mt-1 pt-1">
                    <NoteRow
                      label="Utgående nedskrivningar"
                      value={closingWriteDowns}
                      computed
                      bold
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Show more/less toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showAdvanced ? (
              <>
                <ChevronUp size={16} />
                Visa färre fält
              </>
            ) : (
              <>
                <ChevronDown size={16} />
                Visa fler fält (rörelseförvärv, uppskrivningar, nedskrivningar m.m.)
              </>
            )}
          </button>

          {/* Carrying amount / Balance validation */}
          <div className={`rounded-lg px-4 py-3 border ${isBalanced ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isBalanced ? (
                  <CheckCircle2 size={18} className="text-green-600" />
                ) : (
                  <AlertCircle size={18} className="text-amber-600" />
                )}
                <span className={`text-sm font-medium ${isBalanced ? "text-green-800" : "text-amber-800"}`}>
                  Redovisat värde (bokfört netto)
                </span>
              </div>
              <span className={`text-sm font-semibold tabular-nums ${isBalanced ? "text-green-800" : "text-amber-800"}`}>
                {formatNumber(carryingAmount)} kr
              </span>
            </div>
            {!isBalanced && (
              <p className="text-xs text-amber-700 mt-1">
                Ska matcha balansräkningen ({formatNumber(sieBalance)} kr). Differens: {formatNumber(balanceDiff)} kr.
              </p>
            )}
            {isBalanced && (
              <p className="text-xs text-green-700 mt-1">
                Matchar redovisat värde i balansräkningen.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Default empty data
export function createEmptyFixedAssetNote(): FixedAssetNoteDetail {
  return {
    openingAcquisitionCost: 0,
    purchases: 0,
    sales: 0,
    openingDepreciation: 0,
    yearDepreciation: 0,
    depreciationMethod: "Linjär",
    usefulLife: 5,
  };
}

/**
 * Extract fixed asset note data from raw SIE account-level data.
 *
 * In BAS 2024:
 * - Asset cost accounts have debit (positive) balances
 * - Accumulated depreciation accounts have credit (negative) balances
 *
 * From opening/closing balances we can derive:
 * - Opening/closing acquisition cost (sum of positive balances)
 * - Opening/closing accumulated depreciation (abs of negative balances)
 * - Net purchases = closing acq - opening acq (approximate)
 * - Year's depreciation = closing depr - opening depr
 */
export function extractFixedAssetFromSie(
  openingBalances: Array<{ accountNumber: string; yearIndex: number; amount: number }>,
  closingBalances: Array<{ accountNumber: string; yearIndex: number; amount: number }>,
  accountRangeStart: number,
  accountRangeEnd: number,
  depreciationMethod?: string,
  usefulLife?: number,
): FixedAssetNoteDetail | null {
  const inRange = (accNum: string) => {
    const num = parseInt(accNum);
    return num >= accountRangeStart && num <= accountRangeEnd;
  };

  const openings = openingBalances.filter(b => b.yearIndex === 0 && inRange(b.accountNumber));
  const closings = closingBalances.filter(b => b.yearIndex === 0 && inRange(b.accountNumber));

  // No data in this range
  if (openings.length === 0 && closings.length === 0) return null;

  // Positive amounts = acquisition cost accounts, negative = accumulated depreciation
  const openingAcquisitionCost = openings
    .filter(b => b.amount > 0)
    .reduce((sum, b) => sum + b.amount, 0);
  const openingDepreciation = Math.abs(
    openings
      .filter(b => b.amount < 0)
      .reduce((sum, b) => sum + b.amount, 0)
  );

  const closingAcquisitionCost = closings
    .filter(b => b.amount > 0)
    .reduce((sum, b) => sum + b.amount, 0);
  const closingDepreciation = Math.abs(
    closings
      .filter(b => b.amount < 0)
      .reduce((sum, b) => sum + b.amount, 0)
  );

  // Derive net change in acquisition cost (purchases - sales)
  const netAcquisitionChange = closingAcquisitionCost - openingAcquisitionCost;
  const purchases = netAcquisitionChange > 0 ? netAcquisitionChange : 0;
  const sales = netAcquisitionChange < 0 ? Math.abs(netAcquisitionChange) : 0;

  // Year's depreciation = change in accumulated depreciation
  const yearDepreciation = closingDepreciation - openingDepreciation;

  return {
    openingAcquisitionCost,
    purchases,
    sales,
    openingDepreciation,
    yearDepreciation: yearDepreciation > 0 ? yearDepreciation : 0,
    depreciationMethod: depreciationMethod || "Linjär",
    usefulLife: usefulLife || 5,
  };
}
